import { create } from 'zustand'
import { UserResponse } from '@/types/user'
import { exitImpersonation, getUserInfo } from '@/service/api/auth'

const IMPERSONATION_ORIGIN_TOKEN_KEY = 'impersonation_origin_token'

interface UserState {
  token: string | null
  user: UserResponse | null
  isAuthenticated: boolean

  // Actions
  login: (token: string, user: UserResponse) => void
  logout: () => void
  startImpersonation: (token: string) => Promise<void>
  exitImpersonationMode: () => Promise<void>
  fetchUser: () => Promise<void>
  init: () => Promise<void>
  updateBalance: (balance: number) => void
}

export const useUserStore = create<UserState>((set, get) => ({
  token: null,
  user: null,
  isAuthenticated: false,

  login: (token, user) => {
    localStorage.setItem('token', token)
    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY)
    set({ token, user, isAuthenticated: true })
  },

  logout: () => {
    localStorage.removeItem('token')
    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY)
    set({ token: null, user: null, isAuthenticated: false })
  },

  startImpersonation: async (token) => {
    const currentToken = localStorage.getItem('token')
    const existingOriginToken = localStorage.getItem(IMPERSONATION_ORIGIN_TOKEN_KEY)
    let createdOriginToken = false

    if (currentToken && !existingOriginToken) {
      localStorage.setItem(IMPERSONATION_ORIGIN_TOKEN_KEY, currentToken)
      createdOriginToken = true
    }

    localStorage.setItem('token', token)
    set({ token, isAuthenticated: true })

    try {
      const user = await getUserInfo({ skip401Redirect: true })
      set({ user, token, isAuthenticated: true })
    } catch (error) {
      if (currentToken) {
        localStorage.setItem('token', currentToken)
      } else {
        localStorage.removeItem('token')
      }

      if (createdOriginToken) {
        localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY)
      }

      set({
        token: currentToken,
        user: get().user,
        isAuthenticated: Boolean(currentToken),
      })

      throw error
    }
  },

  exitImpersonationMode: async () => {
    try {
      await exitImpersonation()
    } catch (error) {
      console.error('Failed to revoke impersonation session:', error)
    }

    localStorage.removeItem('token')
    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY)
    set({ token: null, user: null, isAuthenticated: false })
  },

  fetchUser: async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      set({ token: null, user: null, isAuthenticated: false })
      return
    }

    try {
      const user = await getUserInfo({ skip401Redirect: true })
      set({ user, token, isAuthenticated: true })
    } catch (error) {
      const originToken = localStorage.getItem(IMPERSONATION_ORIGIN_TOKEN_KEY)

      if (originToken && originToken !== token) {
        localStorage.setItem('token', originToken)
        localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY)

        try {
          const originUser = await getUserInfo({ skip401Redirect: true })
          set({ user: originUser, token: originToken, isAuthenticated: true })
          return
        } catch (restoreError) {
          console.error('Failed to restore origin user session:', restoreError)
        }
      }

      // If 401, getUserInfo (via request) might have already redirected or thrown.
      // We should clear state if token is invalid.
      localStorage.removeItem('token')
      localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY)
      set({ token: null, user: null, isAuthenticated: false })
      console.error('Failed to fetch user info:', error)
    }
  },

  init: async () => {
    const token = localStorage.getItem('token')
    if (token) {
      set({ token, isAuthenticated: true })
      // Verify token and get user info
      await get().fetchUser()
    }
  },

  updateBalance: (balance) => {
    const user = get().user
    if (!user) return
    set({ user: { ...user, balance } })
  },
}))
